import { WTypography } from '../../wrappers/wrapper';
import { useEffect, useRef, useState } from 'react';
import Slide from '@mui/material/Slide';
import style from './search-all-modal.module.scss';
import SearchItemsList from '../../organisms/search-items-list/search-items-list';
import { IQueryParams, TArticle, TComment, TUser } from '../../../models/common';
import dbService from '../../../services/database.service';
import SearchHeader from 'components/organisms/search-header/search-header';

interface SearchAllModalProps {
  open: boolean;
  searchText: string;
  handleCloseForSearchModal: () => void;
}

let lastFetchTime = Date.now();
const FETCH_LIMIT = 12;
const tabs = ['Articles', 'Comments', 'Members'];
const items: any = {
  0: 'articles',
  1: 'comments',
  2: 'users',
};

function SearchAllModal({ open, searchText, handleCloseForSearchModal }: SearchAllModalProps) {
  const isFirstRender = useRef(true);

  const defaultFilter: IQueryParams = {
    relation: true,
    limit: FETCH_LIMIT,
    sort: { _id: -1 },
    filter: {},
  };
  const [hasMore, setHasMore] = useState(true);
  const [query, setQuery] = useState(searchText);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [data, setData] = useState<TUser[] | TComment[] | TArticle[]>([]);
  const [skip, setSkip] = useState(0);
  const [filter, setFilter] = useState<IQueryParams>(defaultFilter);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    fetchData();
  }, [filter]);

  useEffect(() => {
    setData([]);
    setHasMore(true);
    setSkip(0);

    setFilter({
      ...defaultFilter,
      filter: getFilterByTab(items[activeTab]),
    });
  }, [activeTab, query]);

  const onTabChangeHandler = (index: number) => {
    setActiveTab(index);
  };

  const fetchData = async () => {
    if (!canFetch()) return;
    try {
      setLoading(true);
      const res = await getFetchItem();
      setSkip((prevSkip) => prevSkip + FETCH_LIMIT);
      setData((prev) => [...prev, ...res]);

      if (res.length < FETCH_LIMIT) {
        setHasMore(false);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    handleCloseForSearchModal();
  };

  const onChange = (value: string) => {
    setQuery(value);
  };

  const getFilterByTab = (tab: string) => {
    if (!query) return {};
    const searchOptions = { $regex: query, $options: 'i' };

    switch (tab) {
      case 'users':
        return { username: searchOptions };
      case 'comments':
        return { message: searchOptions };
      default:
        return { $or: [{ title: searchOptions }, { content: searchOptions }] };
    }
  };

  const getFetchItem = () => {
    switch (items[activeTab]) {
      case 'users':
        return dbService.getUsers({ ...filter, skip });
      case 'comments':
        return dbService.getComments({ ...filter, skip });
      default:
        return dbService.getArticles({ ...filter, skip });
    }
  };

  const canFetch = () => {
    if (Date.now() - lastFetchTime > 10) {
      lastFetchTime = Date.now();
      return true;
    }
  };

  return (
    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
      <div className={style.container}>
        <SearchHeader
          value={query}
          loading={loading}
          onChange={onChange}
          debounce={500}
          onClose={handleClose}
        />
        <div className={style.content}>
          <div className={style.searchResultsTitle}>Search results for "{query}"</div>
          <div className={style.tabs}>
            {tabs.map((tab, index) => (
              <WTypography
                key={index}
                className={`${style.tab} ${index === activeTab && style.active}`}
                onClick={() => onTabChangeHandler(index)}
              >
                {tab}
              </WTypography>
            ))}
          </div>
          <div className={style.searchResults}>
            <SearchItemsList hasMore={hasMore} data={data} fetchData={fetchData} />
          </div>
        </div>
      </div>
    </Slide>
  );
}

export default SearchAllModal;
